import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col } from "antd";
import ConvertToLocalTimezone from "/app/src/components/generic/formatting/dateTime";
import { fileService } from "/app/src/services";
import { File as FileType } from "/app/src/models";
import { Button, useDisclosure, Modal, Divider } from "@heroui/react";
import RedButton from "/app/src/components/HeroUi/RedButton";
import ModalBuilder from "/app/src/components/HeroUi/helpers/ModalBuilder";

type RefType = {
  locationFound: boolean;
  setLocationFound: (value: boolean) => void;
};

/**
 * File component that handles file operations.
 * @param {object} props - The component props.
 * @param {FileType} props.file - The file object.
 * @param {(file: FileType) => Promise<any>|undefined} props.removeFile - Function to remove the file.
 * @param {(file: FileType) => void} props.updateFile - Function to update the file.
 * @returns {JSX.Element} The File component.
 */
export default function File({
  file,
  removeFile,
  updateFile,
}: {
  file: FileType;
  removeFile: (file: FileType) => Promise<any> | undefined;
  updateFile: (file: FileType) => void;
}) {
  const { t } = useTranslation();
  const [locationFound, setLocationFound] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const { onOpenChange } = useDisclosure();

  //refs needed when using setInterval
  const refVisible = useRef<RefType | null>(null);
  refVisible.current = { locationFound, setLocationFound };

  useEffect(() => {
    setLocationFound(Boolean(file.location));
  }, [file]);

  useEffect(() => {
    const id = setInterval(() => {
      if (refVisible?.current && !refVisible.current.locationFound) {
        if (!file.id) return;
        fileService.getSingle(file.id).then((response) => {
          if (response.file.location) {
            updateFile(response.file);
          }
        });
      }
    }, 10000); //10 seconds
    return () => {
      clearInterval(id);
    };
  }, [updateFile, file.id]);

  /**
   * HandleDelete function that removes the file and toggles open change.
   * @returns {void}
   */
  const HandleDelete = useCallback(() => {
    removeFile(file);
    onOpenChange();
  }, [file, removeFile, onOpenChange]);

  /**
   * Closes the delete modal.
   * @returns {void}
   */
  const closeDeleteModalHandler = useCallback(() => {
    setIsDeleteModalOpen(false);
  }, []);

  /**
   * Opens the delete modal.
   * @returns {void}
   */
  const openDeleteModalHandler = useCallback(() => {
    setIsDeleteModalOpen(true);
  }, []);

  /**
   * Closes the download modal.
   * @returns {void}
   */
  const closeDownloadModalHandler = useCallback(() => {
    setIsDownloadModalOpen(false);
  }, []);

  /**
   * Opens the download modal.
   * @returns {void}
   */
  const openDownloadModalHandler = useCallback(() => {
    setIsDownloadModalOpen(true);
  }, []);

  /**
   * Handles file download request.
   * @returns {Promise<any>} A promise that resolves when the download is complete.
   */
  const HandleDownloadClick = useCallback(async () => {
    //retrieve file and download popup
    // xlsx files have an octet stream buffer response type
    // axios needs to know the expected response
    // type is different
    let buffer = false;
    if (file.fileType === ".xlsx") {
      buffer = true;
    }
    return await fileService.downloadFile(file.id, buffer).then((response) => {
      //code based off of https://gist.github.com/bierik/0baa0de30cc4ee6d3fbf8485c4d12bb8
      const contentDisposition = response.headers[
        "content-disposition"
      ] as string;
      const contentType = response.headers["content-type"];
      const filename = contentDisposition.split("filename=")[1].split(";")[0];
      const file = new Blob([response.data], { type: contentType });
      if ("msSaveOrOpenBlob" in window.navigator) {
        (window.navigator as any).msSaveOrOpenBlob(file, filename);
      }
      // For Firefox and Chrome
      else {
        // Bind blob on disk to ObjectURL
        const data = URL.createObjectURL(file);
        const aTag = document.createElement("a");
        aTag.setAttribute("style", "display: none");
        aTag.href = data;
        aTag.download = filename;
        document.body.appendChild(aTag);
        aTag.click();
        // For Firefox
        setTimeout(() => {
          document.body.removeChild(aTag);
          // Release resource on disk after triggering the download
          window.URL.revokeObjectURL(data);
        }, 100);
      }
    });
  }, [file]);

  /**
   * Handles click event to initiate file download and close the download modal.
   * @returns {void}
   */
  const onClick = useCallback(() => {
    HandleDownloadClick().then(() => {
      closeDownloadModalHandler();
    });
  }, [HandleDownloadClick, closeDownloadModalHandler]);

  return (
    <>
      <Row justify="start" gutter={16}>
        <Col span={16}>
          <h3>
            <ConvertToLocalTimezone
              {...(file.creationDate && { date: file.creationDate })}
            />
          </h3>
          <p>{file.name}</p>
        </Col>
        {file.location ? (
          <Col span={4}>
            <Button
              size="md"
              radius="none"
              variant="bordered"
              color="default"
              onPress={openDownloadModalHandler}
              fullWidth
              className="hover:border-primary-default hover:text-primary-default"
            >
              {t("translation:download")} {t("translation:file")}
            </Button>
            <Modal
              disableAnimation={false}
              isOpen={isDownloadModalOpen}
              onOpenChange={setIsDownloadModalOpen}
              placement="top"
              aria-label="Confirm file download"
            >
              {ModalBuilder({
                warning: false,
                modalHeader: t("translation:confirm_file_download"),
                modalFooter: (
                  <>
                    <Button
                      size="md"
                      radius="none"
                      color="default"
                      variant="bordered"
                      onPress={closeDownloadModalHandler}
                    >
                      {t("translation:cancel")}
                    </Button>
                    <Button
                      size="md"
                      radius="none"
                      color="primary"
                      onPress={onClick}
                    >
                      {t("translation:download")}
                    </Button>
                  </>
                ),
              })}
            </Modal>
          </Col>
        ) : (
          <Col span={4}>
            <h4>{t("translation:generating_wait")}</h4>
          </Col>
        )}
        <Col span={4}>
          {file.location ? (
            <>
              <RedButton size="md" onPress={openDeleteModalHandler} fullWidth>
                {t("translation:delete")} {t("translation:file")}
              </RedButton>
              <Modal
                disableAnimation={false}
                isOpen={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
                placement="top"
                aria-label="Confirm file delete"
              >
                {ModalBuilder({
                  warning: false,
                  modalHeader: t("translation:confirm_file_delete"),
                  modalFooter: (
                    <>
                      <Button
                        size="md"
                        color="default"
                        variant="bordered"
                        onPress={closeDeleteModalHandler}
                      >
                        {t("translation:cancel")}
                      </Button>
                      <RedButton size="md" onPress={HandleDelete}>
                        {t("translation:delete")}
                      </RedButton>
                    </>
                  ),
                })}
              </Modal>
            </>
          ) : (
            <>
              <RedButton size="md" onPress={openDeleteModalHandler} fullWidth>
                {t("translation:cancel")}
              </RedButton>
              <Modal
                disableAnimation={false}
                isOpen={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
                placement="top"
                aria-label="Confirm cancel"
              >
                {ModalBuilder({
                  warning: false,
                  modalHeader: t("translation:confirm_file_delete"),
                  modalFooter: (
                    <>
                      <Button
                        size="md"
                        color="default"
                        variant="bordered"
                        onPress={closeDeleteModalHandler}
                      >
                        {t("translation:cancel")}
                      </Button>
                      <RedButton size="md" onPress={HandleDelete}>
                        {t("translation:delete")}
                      </RedButton>
                    </>
                  ),
                })}
              </Modal>
            </>
          )}
        </Col>
      </Row>
      <Divider />
    </>
  );
}
